import { useEffect, useMemo, useRef, useState } from 'react'
import BigNumber from 'bignumber.js'
import { addCustomTokens, fetchBithompTokenInfo, fetchTokenPriceUsd, getTokenBySymbol, useXrplTokens } from '../xrpl'

export const useBescUsdPrice = (): BigNumber | null => {
  const { tokens } = useXrplTokens()
  const bescSymbol = process.env.REACT_APP_BESC_SYMBOL || process.env.REACT_APP_BESC_CURRENCY || 'BESC'
  const bescIssuer = process.env.REACT_APP_BESC_ISSUER || 'rUnjJv3iLPNWTAVXoNUfHD9CBm7tcwkjLa'
  const bescCurrency = process.env.REACT_APP_BESC_CURRENCY || '4245534300000000000000000000000000000000'
  const fetchRef = useRef(false)
  const dexFetchRef = useRef(false)
  const [directPriceUsd, setDirectPriceUsd] = useState<BigNumber | null>(null)

  const bescToken = useMemo(() => {
    return (
      getTokenBySymbol(bescSymbol, tokens) ||
      tokens.find((token) => token.issuer === bescIssuer && token.currency === bescCurrency) ||
      tokens.find((token) => token.symbol === bescSymbol || token.currency === bescSymbol) ||
      null
    )
  }, [bescCurrency, bescIssuer, bescSymbol, tokens])

  const fallbackToken = useMemo(
    () => ({
      currency: bescCurrency,
      issuer: bescIssuer,
      symbol: bescSymbol,
      name: bescSymbol,
      decimals: 6,
    }),
    [bescCurrency, bescIssuer, bescSymbol],
  )

  useEffect(() => {
    const token = bescToken || fallbackToken
    if (!token?.issuer || token.isNative || token.currency === 'XRP') {
      return
    }
    const existingPrice = Number(token.priceUsd)
    if (Number.isFinite(existingPrice) && existingPrice > 0) {
      setDirectPriceUsd(new BigNumber(existingPrice))
      return
    }
    if (fetchRef.current) {
      return
    }
    fetchRef.current = true
    fetchBithompTokenInfo(token)
      .then((info) => {
        if (info) {
          addCustomTokens([info])
        }
        const price = Number(info?.priceUsd)
        if (Number.isFinite(price) && price > 0) {
          setDirectPriceUsd(new BigNumber(price))
        } else {
          fetchRef.current = false
        }
      })
      .catch(() => {
        fetchRef.current = false
      })
  }, [bescToken, fallbackToken])

  useEffect(() => {
    const token = bescToken || fallbackToken
    if (!token?.issuer || token.isNative || token.currency === 'XRP') {
      return
    }
    const existingPrice = Number(token.priceUsd)
    if (Number.isFinite(existingPrice) && existingPrice > 0) {
      return
    }
    if (dexFetchRef.current) {
      return
    }
    dexFetchRef.current = true
    fetchTokenPriceUsd(token)
      .then((price) => {
        if (Number.isFinite(price) && price > 0) {
          setDirectPriceUsd(new BigNumber(price))
        } else {
          dexFetchRef.current = false
        }
      })
      .catch(() => {
        dexFetchRef.current = false
      })
  }, [bescToken, fallbackToken])

  const xrplPriceUsd = useMemo(() => {
    const price = Number(bescToken?.priceUsd)
    if (!Number.isFinite(price) || price <= 0) {
      return null
    }
    return new BigNumber(price)
  }, [bescToken])

  return xrplPriceUsd || directPriceUsd
}
